import { useCallback, useMemo } from 'react';
import {
  CustomerGroupMember,
  dispatch,
  getSelectedBeatPlanIds,
  getWorkspaceId,
  updateSelectedItemIds,
  useAppSelector,
  useLazyGetGroupMembersQuery,
} from '@zonofi/zono-money-store';
import { useDebounceSearch, usePaginatedList } from '../../hooks';

interface BeatPlanCustomer {
  id: string;
  inviteId: string;
  customerId: string;
  customerName: string;
  companyName: string;
  phone?: string;
}

export const useDigitalOrderBeatPlanCustomer = () => {
  const workspaceId = useAppSelector(getWorkspaceId);
  const selectedBeatPlanIds = useAppSelector(getSelectedBeatPlanIds);
  const selectedBeatPlanId = selectedBeatPlanIds?.[0] ?? '';
  const { debouncedSearchValue } = useDebounceSearch();

  const [getBeatPlanCustomersApi, getBeatPlanCustomersResponse] =
    useLazyGetGroupMembersQuery();

  const fetchBeatPlanCustomers = useCallback(
    (params: { pageNo: number; pageSize: number }) => {
      if (!workspaceId || !selectedBeatPlanId) return;

      getBeatPlanCustomersApi({
        sellerWorkspaceId: workspaceId,
        groupId: selectedBeatPlanId,
        pageNo: params.pageNo,
        pageSize: params.pageSize,
      });
    },
    [workspaceId, selectedBeatPlanId]
  );

  const memoizedCustomers = useMemo(
    () => getBeatPlanCustomersResponse?.data?.customerGroupMembers ?? [],
    [getBeatPlanCustomersResponse?.data?.customerGroupMembers]
  );

  const {
    data: beatPlanCustomers,
    pageNo,
    lastItemRef,
    onScroll,
    isPageLoading,
    isEndOfList,
    refresh,
  } = usePaginatedList<CustomerGroupMember>({
    fetchDataApiCall: fetchBeatPlanCustomers,
    apiResponseData: memoizedCustomers,
    isLoading: getBeatPlanCustomersResponse.isLoading,
    isError: getBeatPlanCustomersResponse.isError,
    uniqueKey: 'id',
    dependencies: [debouncedSearchValue],
    pageSize: 20,
  });

  const customers = useMemo<BeatPlanCustomer[]>(
    () =>
      beatPlanCustomers.map((item) => {
        const customerId = item.customerId || item.id;

        return {
          id: customerId,
          inviteId: customerId,
          customerId,
          customerName: item.name || item.companyName || '',
          companyName: item.companyName || item.name,
          phone: item.phone,
        };
      }),
    [beatPlanCustomers]
  );

  const onCustomerSelect = (customer: BeatPlanCustomer) => {
    const customerId = customer?.id || customer?.inviteId;
    if (!customerId) return;
    dispatch(updateSelectedItemIds(customerId));
  };

  const onSelectAllCustomers = useCallback(() => {
    const allCustomerIds = customers
      ?.map((customer) => customer?.id)
      .filter((id): id is string => Boolean(id));

    dispatch(updateSelectedItemIds(allCustomerIds));
  }, [customers]);

  return {
    customers,
    lastItemRef,
    onScroll,
    isPageLoading,
    isEndOfList,
    refresh,
    isLoading:
      pageNo === 1 &&
      (getBeatPlanCustomersResponse.isUninitialized ||
        getBeatPlanCustomersResponse.isFetching),
    onSelectAllCustomers,
    onCustomerSelect,
    selectedBeatPlanIds,
  };
};
